import { useQuery } from '@tanstack/react-query';
import {
  Database,
  ClipboardList,
  Activity,
  Clock,
  FileText,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import api from '../lib/api';
import type { StatSummary, AuditLog, PaginatedResponse } from '../types';
import { format, formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
}

function StatCard({ title, value, icon, color }: StatCardProps) {
  return (
    <div className="card flex items-center gap-4">
      <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${color}`}>
        {icon}
      </div>
      <div>
        <p className="text-sm text-gray-500">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value.toLocaleString('de-AT')}</p>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { data: statsData, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: async () => {
      const [fieldsRes, questsRes, apisRes] = await Promise.allSettled([
        api.get('/data-fields', { params: { limit: 100 } }),
        api.get('/questionnaires', { params: { limit: 100 } }),
        api.get('/api-endpoints', { params: { limit: 100 } }),
      ]);
      const totalFields =
        fieldsRes.status === 'fulfilled'
          ? Array.isArray(fieldsRes.value.data) ? fieldsRes.value.data.length : fieldsRes.value.data?.data?.length ?? 0
          : 0;
      const totalQuestionnaires =
        questsRes.status === 'fulfilled'
          ? Array.isArray(questsRes.value.data) ? questsRes.value.data.length : questsRes.value.data?.data?.length ?? 0
          : 0;
      const apiEndpoints =
        apisRes.status === 'fulfilled'
          ? Array.isArray(apisRes.value.data)
            ? apisRes.value.data
            : apisRes.value.data?.data ?? []
          : [];
      const activeApis = apiEndpoints.filter(
        (ep: any) => ep.isActive === true || ep.status === 'ACTIVE',
      ).length;
      return {
        totalOrganizations: 1,
        totalFields,
        totalEntries: totalQuestionnaires, // reuse as rough count
        totalUsers: 1,
        totalQuestionnaires,
        activeApis,
        pendingSubmissions: 0,
      } as StatSummary;
    },
  });

  const {
    data: logsData,
    isLoading: logsLoading,
  } = useQuery<PaginatedResponse<AuditLog>>({
    queryKey: ['dashboard', 'recent-logs'],
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<AuditLog>>('/logs', {
        params: { limit: 10, sort: 'createdAt:desc' },
      });
      return res.data;
    },
  });

  const formatTimestamp = (dateStr: string): string => {
    const date = new Date(dateStr);
    return format(date, 'dd.MM.yyyy HH:mm', { locale: de });
  };

  const timeAgo = (dateStr: string): string => {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true, locale: de });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <img
          src="/img/oerk-logo.png"
          alt="ÖRK"
          className="h-8 w-8 object-contain"
        />
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-sm text-gray-500">Übersicht über die Datensammlung</p>
        </div>
      </div>

      {/* Statistics Cards */}
      {statsError && (
        <div className="flex items-center gap-2 rounded-lg bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm text-yellow-700">
          <AlertCircle size={18} className="shrink-0" />
          <span>Statistiken konnten nicht geladen werden</span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Datenfelder"
          value={statsData?.totalFields ?? 0}
          icon={<Database size={22} className="text-white" />}
          color="bg-accent-blue"
        />
        <StatCard
          title="Fragebögen"
          value={statsData?.totalQuestionnaires ?? 0}
          icon={<ClipboardList size={22} className="text-white" />}
          color="bg-accent-turquoise-dark"
        />
        <StatCard
          title="Aktive APIs"
          value={statsData?.activeApis ?? 0}
          icon={<Activity size={22} className="text-white" />}
          color="bg-accent-green-dark"
        />
        <StatCard
          title="Offene Einreichungen"
          value={statsData?.pendingSubmissions ?? 0}
          icon={<Clock size={22} className="text-white" />}
          color="bg-accent-orange"
        />
      </div>

      {/* Recent Activity */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <FileText size={20} className="text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">Letzte Aktivitäten</h2>
        </div>

        {logsLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : logsData?.data?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <th className="pb-3 pr-4">Benutzer</th>
                  <th className="pb-3 pr-4">Aktion</th>
                  <th className="pb-3 pr-4">Typ</th>
                  <th className="pb-3 text-right">Zeit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logsData.data.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="py-3 pr-4">
                      <span className="font-medium text-gray-900">
                        {log.user.displayName}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className="text-gray-700 capitalize">
                        {log.action.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="py-3 pr-4">
                      <span className="badge-info">{log.entityType}</span>
                    </td>
                    <td className="py-3 text-right">
                      <span className="text-gray-500" title={formatTimestamp(log.createdAt)}>
                        {timeAgo(log.createdAt)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-400">
            {statsLoading ? 'Lade Aktivitäten...' : 'Keine Aktivitäten vorhanden'}
          </p>
        )}
      </div>
    </div>
  );
}
